import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useView } from '../context/ViewContext';
import { useOrganisme } from '../context/OrganismeContext';
import { usePermissions } from '../hooks/usePermissions';
import { 
  BuildingStorefrontIcon, 
  ArrowLeftIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UsersIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

import GestionResponsables from './miellerie/GestionResponsables';
import GestionCreneaux from './miellerie/GestionCreneaux';
import GestionUsagers from './miellerie/GestionUsagers';
import SuiviPaiements from './miellerie/SuiviPaiements';
import SuiviCheques from './miellerie/SuiviCheques';

export default function GestionMiellerie() {
  const { user } = useAuth();
  const { canAccessMiellerie, canManageMiellerieResponsables, canExportMiellerieResponsableStats, canManageMiellerieActivite, permissions } = usePermissions();
  const navigate = useNavigate();
  const { activeView, switchView, canAccessView } = useView();
  const { organismeActif } = useOrganisme();
  const [activeTab, setActiveTab] = useState('planning');
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [denyReason, setDenyReason] = useState(''); // 'role' | 'souscription' | ''
  const [showMesReservations, setShowMesReservations] = useState(true);
  const [isAlsoResponsable, setIsAlsoResponsable] = useState(false);

  /** Réservations perso = espace adhérent (layout jaune), pas le layout admin. */
  const goToMesReservations = () => {
    if (canAccessView('user')) {
      switchView('user');
    }
    navigate('/miellerie');
  };

  useEffect(() => {
    if (activeView && organismeActif !== undefined) {
      checkUserAccess();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, organismeActif, permissions]);

  const checkUserAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();

      const userRoles = user?.roles || [];
      const isAdmin = userRoles.includes('admin');
      const isSuperAdmin = userRoles.includes('super_admin');
      const hasAMAIR = (user?.organismes || []).includes('AMAIR');

      let alsoResponsable = false;
      let responsableSansSouscription = false;
      let derogationAcces = false;
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/miellerie/responsables/mon-statut`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { annee: year },
          }
        );
        alsoResponsable = Boolean(data.estResponsable && data.accesGestion);
        responsableSansSouscription = Boolean(data.estResponsable && !data.accesGestion);
        derogationAcces = Boolean(data.derogationAcces);
      } catch (err) {
        alsoResponsable = false;
      }

      // Super admin, ou admin AMAIR avec permission d'accès (sans souscription usager)
      if (isSuperAdmin || (isAdmin && hasAMAIR && canAccessMiellerie())) {
        setUserRole('admin_amair');
        setIsAlsoResponsable(alsoResponsable);
        setActiveTab('planning');
        setHasAccess(true);
        setDenyReason('');
        setShowMesReservations(true);
        setLoading(false);
        return;
      }

      setIsAlsoResponsable(false);

      // Responsable nommé (y compris admin ADAR avec dérogation)
      if (alsoResponsable) {
        setUserRole('responsable');
        setActiveTab('planning');
        setHasAccess(true);
        setDenyReason('');
        setShowMesReservations(!derogationAcces);
        setLoading(false);
        return;
      }

      if (responsableSansSouscription) {
        setHasAccess(false);
        setDenyReason('souscription');
        setLoading(false);
        return;
      }

      if (isAdmin) {
        setHasAccess(false);
        setDenyReason('role');
        setLoading(false);
        return;
      }

      setHasAccess(false);
      setDenyReason('role');
      setLoading(false);
    } catch (err) {
      console.error('Erreur vérification accès:', err);
      setHasAccess(false);
      setDenyReason('role');
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'planning', label: 'Réservations', icon: CalendarIcon },
    { id: 'usagers', label: 'Usagers', icon: UsersIcon },
    ...(userRole === 'responsable'
      ? [{ id: 'paiements', label: 'Suivi des paiements', icon: BanknotesIcon }]
      : []),
    ...(userRole === 'admin_amair' ? [
      { id: 'cheques', label: 'Chèques', icon: BanknotesIcon },
      { id: 'responsables', label: 'Responsables', icon: UserGroupIcon },
    ] : []),
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center text-honey-600 hover:text-honey-700 mb-8">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </Link>

          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-honey-100 rounded-2xl mb-6">
              <BuildingStorefrontIcon className="h-8 w-8 text-honey-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Gestion de la miellerie
            </h1>
          </div>

          <div className="bg-honey-50 border border-honey-200 rounded-xl p-8 text-center max-w-2xl mx-auto">
            <ExclamationTriangleIcon className="h-12 w-12 text-honey-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-honey-900 mb-2">
              Accès non autorisé
            </h2>
            {denyReason === 'souscription' ? (
              <>
                <p className="text-honey-800 mb-6">
                  Vous devez avoir une adhésion AMAIR active et une souscription miellerie
                  pour l&apos;année en cours afin d&apos;accéder à la gestion.
                </p>
                <Link
                  to="/mes-adhesions"
                  className="inline-flex items-center px-6 py-3 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors"
                >
                  Retour vers les adhésions
                </Link>
              </>
            ) : (
              <>
                <p className="text-honey-800 mb-6">
                  Cette page est réservée aux administrateurs AMAIR et aux responsables de la
                  miellerie disposant d&apos;une souscription active.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center px-6 py-3 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors"
                >
                  Retour à l&apos;accueil
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <span className="font-medium text-gray-900">
            {userRole === 'admin_amair' ? 'Administration' : 'Espace responsable'}
          </span>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>Miellerie collective</span>
        </div>
      </div>

      {/* Header */}
      <div className="px-4 sm:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion de la miellerie</h1>
            <p className="mt-2 text-sm text-gray-600">
              Plages d&apos;ouverture, réservations du jour et encaissements
            </p>
          </div>
          {showMesReservations && (
          <button
            type="button"
            onClick={goToMesReservations}
            className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-honey-700 hover:text-honey-800"
          >
            <svg className="h-4 w-4 rotate-180" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            Mes réservations
          </button>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-8 pb-8 space-y-6">
        <div className="border-b border-gray-200 flex items-center justify-between">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 sm:px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-honey-600 text-honey-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="">
          {activeTab === 'cheques' && userRole === 'admin_amair' && <SuiviCheques />}
          {activeTab === 'responsables' && userRole === 'admin_amair' && (
            <GestionResponsables
              canManageResponsables={canManageMiellerieResponsables()}
              canExportStats={canExportMiellerieResponsableStats()}
            />
          )}
          {activeTab === 'usagers' && (
            <GestionUsagers
              canManageActivite={
                userRole === 'responsable' || canManageMiellerieActivite() || isAlsoResponsable
              }
            />
          )}
          {activeTab === 'paiements' && userRole === 'responsable' && <SuiviPaiements />}
          {activeTab === 'planning' && (
            <GestionCreneaux
              canAssignResponsable={userRole === 'admin_amair' && canManageMiellerieActivite()}
              canManageActivite={
                userRole === 'responsable' || canManageMiellerieActivite() || isAlsoResponsable
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
